import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import classNames from 'classnames';

import { IPressPostFields } from '../../../types/contentful';

import { ColorTokenProp } from '../../theme/tokens/color';

import { InlineAnchor } from '../Link';

interface Props extends IPressPostFields {
  color: ColorTokenProp;
  focused?: boolean;
}

export function PressPost({
  title,
  preview,
  featured,
  sourceName,
  logo,
  color,
  linkToSource,
  focused = false
}: Props) {
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (focused) linkRef.current?.focus();
  }, [focused]);

  const { file, description } = logo?.fields ?? {};
  const dimensions = file?.details?.image;
  const width = dimensions?.width ?? 120;
  const height = dimensions?.height ?? 30;

  return (
    <div
      className="box-border overflow-hidden rounded-2xl bg-cool-neutral-100 shadow-sm desktop:mt-1"
      role="group"
      aria-label="post"
    >
      <div
        className={classNames(
          'box-border rounded-t-2xl p-5 desktop:mt-1',
          color === 'pressHeaderBg1' ? 'bg-peppermint' : 'bg-navy'
        )}
      >
        {file && featured ? (
          <div className="flex h-10 items-center justify-around">
            <Image
              src={`https:${file.url}`}
              alt={description ?? 'Press company logo that featured Greenlight'}
              width={width}
              height={height}
              loading="lazy"
              className="h-[30px] w-auto"
              sizes="120px"
            />
          </div>
        ) : (
          <h3 className="typography-display-5 !mb-0 text-center text-white">
            {sourceName}
          </h3>
        )}
      </div>
      <div className="box-border pb-10 pl-6 pr-6 pt-6 tablet:pb-12 tablet:pl-8 tablet:pr-8 tablet:pt-8">
        <div className="pb-4">
          <h3 className="typography-display-4 !mb-0">{title}</h3>
        </div>
        {preview && (
          <div className="pb-4">
            <p className="typography-body-1">{preview}</p>
          </div>
        )}
        {linkToSource && (
          <InlineAnchor href={linkToSource} target="_blank" ref={linkRef}>
            Read more
          </InlineAnchor>
        )}
      </div>
    </div>
  );
}
